import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ideationStatusLabel, viabilityLabel, type Ideation, type Project } from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { CommunitiesPanel } from '@/features/community/CommunitiesPanel';
import { fetchUpcomingAgendaEvents, formatAgendaRange } from '@/features/agenda/agendaEvents';
import { useToast } from '@/features/ui/toast/useToast';

const QUICK_LINKS = [
  {
    to: '/app/conception',
    title: 'Conception',
    description: 'Idéation, diagnostic IGPE et modélisation BMC / BP.',
    tone: 'blue',
    step: '01',
    cta: 'Ouvrir',
  },
  {
    to: '/app/accompagnement',
    title: 'Accompagnement',
    description: 'Profil, engagement, feuille de route et suivi stratégique.',
    tone: 'green',
    step: '02',
    cta: 'Continuer',
  },
  {
    to: '/app/planification',
    title: 'Planification',
    description: 'Risques, préparation et déploiement opérationnel.',
    tone: 'orange',
    step: '03',
    cta: 'Voir',
  },
  {
    to: '/app/pilotage',
    title: 'Pilotage',
    description: 'Pilotage stratégique et pilotage des ressources.',
    tone: 'navy',
    step: '04',
    cta: 'Voir',
  },
  {
    to: '/app/marketplace-crm',
    title: 'Marketplace & CRM',
    description: 'Boutique, pipeline clients et assistant marketing.',
    tone: 'teal',
    step: '05',
    cta: 'Explorer',
  },
] as const;

export function DashboardPage() {
  const { profile } = useAuth();
  const toast = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [ideations, setIdeations] = useState<Ideation[]>([]);
  const [agenda, setAgenda] = useState<
    { id: string; title: string; starts_at: string | null; ends_at: string | null }[]
  >([]);

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
  }, []);

  const welcomeName = useMemo(() => {
    const first = profile?.first_name?.trim();
    if (first) return first;
    const last = profile?.last_name?.trim();
    if (last) return last;
    const emailName = profile?.email?.split('@')[0];
    return emailName || 'entrepreneur';
  }, [profile]);

  const retained = ideations.filter((i) => i.status === 'retained' || i.status === 'project_linked').length;
  const viable = projects.filter((p) => p.viability === 'viable').length;
  const nextAction =
    ideations.length === 0 ? 'Lancer une idée' : projects.length === 0 ? 'Modéliser' : 'Piloter';

  useEffect(() => {
    if (!profile) return;
    void (async () => {
      const [p, i, a] = await Promise.all([
        kuunda.from('projects').select('*').eq('user_id', profile.id).order('created_at', { ascending: false }),
        kuunda.from('ideations').select('*').eq('user_id', profile.id).order('created_at', { ascending: false }),
        fetchUpcomingAgendaEvents(5),
      ]);
      if (p.error || i.error) {
        toast.error(p.error?.message || i.error?.message || 'Erreur de chargement');
        return;
      }
      setProjects((p.data ?? []) as Project[]);
      setIdeations((i.data ?? []) as Ideation[]);
      setAgenda(a);
    })();
  }, [profile, toast]);

  return (
    <div className="u-page">
      <div className="u-hero u-hero--dash">
        <div>
          <p className="u-welcome">
            {greeting}, <span className="u-welcome-name">{welcomeName}</span>
          </p>
          <p className="u-subtitle">
            Voici l’état de votre parcours entrepreneurial ISBD — continuez là où vous vous êtes arrêté.
          </p>
        </div>
        <div className="u-actions">
          <Link className="btn btn-primary" to="/app/conception">
            Nouvelle conception
          </Link>
          <Link className="btn btn-ghost" to="/app/accompagnement">
            Accompagnement
          </Link>
        </div>
      </div>

      <div className="u-kpi-grid">
        <article className="u-kpi u-kpi--blue">
          <p className="u-kpi-label">Idéations</p>
          <p className="u-kpi-value">{ideations.length}</p>
          <p className="u-kpi-meta">{retained} retenues</p>
        </article>
        <article className="u-kpi u-kpi--green">
          <p className="u-kpi-label">Projets</p>
          <p className="u-kpi-value">{projects.length}</p>
          <p className="u-kpi-meta">{viable} viables</p>
        </article>
        <article className="u-kpi u-kpi--orange">
          <p className="u-kpi-label">Agenda</p>
          <p className="u-kpi-value">{agenda.length}</p>
          <p className="u-kpi-meta">prochains événements</p>
        </article>
        <article className="u-kpi u-kpi--navy">
          <p className="u-kpi-label">Prochaine action</p>
          <p className="u-kpi-value u-kpi-value--text">{nextAction}</p>
          <p className="u-kpi-meta">recommandée</p>
        </article>
      </div>

      <CommunitiesPanel />

      <section className="u-parcours">
        <div className="u-section-head">
          <h3 className="u-section-title">Aperçu de votre parcours</h3>
          <p className="u-section-lead">Les étapes clés pour construire et piloter votre entreprise.</p>
        </div>
        <div className="u-modules">
          {QUICK_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className={`u-module u-module--${l.tone}`}>
              <div className="u-module-top">
                <span className="u-module-step">{l.step}</span>
                <div className={`u-module-icon ${l.tone}`} aria-hidden>
                  {l.title.charAt(0)}
                </div>
              </div>
              <h3>{l.title}</h3>
              <p>{l.description}</p>
              <span className="u-module-cta">{l.cta}</span>
            </Link>
          ))}
        </div>
      </section>

      <div className="u-dash-split">
        <section className="u-card">
          <div className="u-card-head">
            <h3 style={{ margin: 0 }}>Idéations récentes</h3>
            <Link className="btn btn-ghost" to="/app/conception">
              Voir tout
            </Link>
          </div>
          {ideations.length === 0 ? (
            <p className="u-empty">Aucune idéation pour le moment.</p>
          ) : (
            <ul className="u-list">
              {ideations.slice(0, 6).map((it) => (
                <li key={it.id} className="u-list-item">
                  <div>
                    <strong>{it.business_idea}</strong>
                    <div className="muted" style={{ fontSize: '0.88rem' }}>
                      {it.solution_code ? `${it.solution_code} · ` : ''}
                      <span className="chip">{ideationStatusLabel(it.status)}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="u-card">
          <div className="u-card-head">
            <h3 style={{ margin: 0 }}>Projets</h3>
            <Link className="btn btn-ghost" to="/app/projets">
              Mes projets
            </Link>
          </div>
          {projects.length === 0 ? (
            <p className="u-empty">Complétez BMC + business plan pour officialiser un projet.</p>
          ) : (
            <ul className="u-list">
              {projects.slice(0, 6).map((pr) => (
                <li key={pr.id} className="u-list-item">
                  <div>
                    <strong>{pr.title}</strong>
                    <div className="muted" style={{ fontSize: '0.88rem' }}>
                      <span className={`chip ${pr.viability === 'viable' ? 'ok' : 'warn'}`}>
                        {viabilityLabel(pr.viability)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {agenda.length > 0 ? (
        <section className="u-card">
          <h3 className="u-section-title" style={{ marginTop: 0 }}>
            Agenda ISBD
          </h3>
          <ul className="u-list">
            {agenda.map((ev) => (
              <li key={ev.id} className="u-list-item">
                <div>
                  <strong>{ev.title}</strong>
                  <div className="muted">{formatAgendaRange(ev.starts_at, ev.ends_at)}</div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
